/**
 * MUI Component overrides for Bare Biologics
 * 
 * Centralizes all component styling to ensure consistency
 */

import { alpha } from '@mui/material/styles';

export const components = (portalTheme) => ({
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: 'none',
        
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        
        '&:active': {
          transform: 'translateY(0)',
        },
        
        '&:disabled': {
          backgroundColor: '#e5e7eb',
          color: '#9ca3af',
        },
      },
      
      contained: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      },
      
      containedPrimary: {
        backgroundColor: portalTheme.primary,
        color: portalTheme.primaryContrast,
        
        '&:hover': {
          backgroundColor: portalTheme.primaryDark,
        },
      },
      
      outlined: {
        borderWidth: 2,
        
        '&:hover': {
          borderWidth: 2,
          backgroundColor: alpha(portalTheme.primary, 0.08),
        },
      },
      
      outlinedPrimary: {
        borderColor: portalTheme.primary,
        color: portalTheme.primary,
        
        '&:hover': {
          borderColor: portalTheme.primaryDark,
          backgroundColor: alpha(portalTheme.primary, 0.08),
        },
      },
      
      text: {
        '&:hover': {
          backgroundColor: alpha(portalTheme.primary, 0.08),
        },
      },
      
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '1rem',
      },
      
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.8125rem',
      },
    },
  },
  
  // Card overrides
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        
        '&:hover': {
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      },
    },
  },
  
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 24,
        
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },
  
  // TextField overrides
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 10,
          transition: 'all 0.2s',
          backgroundColor: '#ffffff',
          
          '&:hover': {
            backgroundColor: '#f9fafb',
            
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: portalTheme.primary,
              borderWidth: 2,
            },
          },
          
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: portalTheme.primary,
              borderWidth: 2,
            },
          },
          
          '&.Mui-error': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ef4444',
            },
          },
        },
        
        '& .MuiInputLabel-root': {
          color: '#6b7280',
          
          '&.Mui-focused': {
            color: portalTheme.primary,
          },
          
          '&.Mui-error': {
            color: '#ef4444',
          },
        },
      },
    },
  },
  
  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.025em',
        border: '1px solid transparent',
      },
      
      colorDefault: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        border: '1px solid #e5e7eb',
      },
      
      colorPrimary: {
        backgroundColor: alpha(portalTheme.primary, 0.1),
        color: portalTheme.primaryDark,
        border: `1px solid ${alpha(portalTheme.primary, 0.3)}`,
      },
      
      colorSuccess: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        border: '1px solid #a7f3d0',
      },
      
      colorError: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fecaca',
      },
      
      colorWarning: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fde68a',
      },
      
      colorInfo: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        border: '1px solid #bfdbfe',
      },
    },
  },
  
  // Table overrides
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          backgroundColor: '#f8fafc',
          color: '#475569',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          borderBottom: '2px solid #e2e8f0',
          padding: '16px',
        },
      },
    },
  },
  
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          transition: 'background-color 0.2s',
          
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
          
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
  },
  
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid #f1f5f9',
        padding: '16px',
        fontSize: '0.875rem',
      },
    },
  },
  
  // Paper overrides
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        backgroundImage: 'none',
      },
      
      elevation1: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      },
      
      elevation2: {
        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      },
      
      elevation3: {
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      },
    },
  },
  
  // AppBar overrides
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        backgroundColor: portalTheme.navbar?.background || portalTheme.primary,
      },
    },
  },
  
  // Drawer overrides
  MuiDrawer: {
    styleOverrides: {
      paper: {
        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
        borderRight: 'none',
        backgroundColor: portalTheme.sidebar?.background || '#ffffff',
      },
    },
  },
  
  // Avatar overrides
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 700,
        fontSize: '1rem',
        backgroundColor: portalTheme.primary,
        color: portalTheme.primaryContrast,
      },
    },
  },
  
  // Divider overrides
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: '#e2e8f0',
      },
    },
  },
  
  // ListItemButton overrides
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        marginBottom: 4,
        transition: 'all 0.2s',
        
        '&:hover': {
          backgroundColor: portalTheme.sidebar?.hoverItem || 'rgba(0, 0, 0, 0.04)',
        },
        
        '&.Mui-selected': {
          backgroundColor: portalTheme.sidebar?.activeItem || alpha(portalTheme.primary, 0.1),
          color: portalTheme.sidebar?.activeText || portalTheme.primary,
          
          '&:hover': {
            backgroundColor: portalTheme.sidebar?.activeItem || alpha(portalTheme.primary, 0.15),
          },
          
          '& .MuiListItemIcon-root': {
            color: portalTheme.sidebar?.activeText || portalTheme.primary,
          },
        },
      },
    },
  },
  
  // Alert overrides
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        fontSize: '0.875rem',
      },
      
      standardSuccess: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        border: '1px solid #a7f3d0',
        
        '& .MuiAlert-icon': {
          color: '#10b981',
        },
      },
      
      standardError: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fecaca',
        
        '& .MuiAlert-icon': {
          color: '#ef4444',
        },
      },
      
      standardWarning: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fde68a',
        
        '& .MuiAlert-icon': {
          color: '#f59e0b',
        },
      },
      
      standardInfo: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        border: '1px solid #bfdbfe',
        
        '& .MuiAlert-icon': {
          color: '#0ea5e9',
        },
      },
    },
  },
  
  // Tooltip overrides
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#1f2937',
        fontSize: '0.75rem',
        borderRadius: 6,
        padding: '8px 12px',
      },
      
      arrow: {
        color: '#1f2937',
      },
    },
  },
  
  // Menu overrides
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        marginTop: 8,
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      },
    },
  },
  
  // LinearProgress overrides
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        height: 8,
        backgroundColor: '#e5e7eb',
      },
      
      bar: {
        borderRadius: 6,
        background: portalTheme.commission?.earned 
          ? `linear-gradient(90deg, ${portalTheme.commission.earned}, ${alpha(portalTheme.commission.earned, 0.8)})`
          : portalTheme.primary,
      },
    },
  },
  
  // Dialog overrides
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      },
    },
  },
  
  // Skeleton overrides (for loading states)
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
      },
      
      wave: {
        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.04), transparent)',
        },
      },
    },
  },
});
